import { ISearchParameters, ITableReport } from "./TableInterfaces";
import { OrderStatus } from "./orderInterfaces";
import {
  IOrderVolumeSummaryItem,
  StepInstrumentType,
  StepOptionOrderType,
  StepOrderSide,
  VolumeCalculationType,
} from "./stepInterfaces";

export interface IManualAddOrderFormValues {
  userIds: number[];
  orderSide: StepOrderSide;
  optionOrderType: StepOptionOrderType | null;
  instrumentType: StepInstrumentType | null;
  instrumentId: string | null;
  fromPrice: number;
  toPrice: number;
  stepCount: number;
  volume: number;
  volumeCalculationType: number;
  autoSellFund?: boolean;
  scheduledExecutionTime?: string | null;
  orderStartegyStr?: string;
}

export interface IFundOrderTableSearchParams {
  sahraUserId?: number | null;
  instrumentId?: string | null;
  portfolioGroupId?: number | null;
}

export interface IFundOrderTableReport extends ITableReport {
  name: string;
  instrumentName: string;
  instrumentIsin: string;
  volume: number;
  price: number;
  timeStr: string;
  auto: boolean;
  previcePurchacingPower: number;
  done: number | null;
  errorCount: number | null;
}

export interface IManualOrderTableReport extends ITableReport {
  orderSide: string;
  sahraName: string;
  operator: string;
  namad: string;
  optionType: string;
  portfoAmount: number;
  totalSize: number;
  firstStep: number;
  lastStep: number;
  stepCount: number;
  activeStep: number;
  filledVolume: number;
  instrumentType: StepInstrumentType;
  instrument: string;
  baseInstrument: string;
  optionInstrument: string;
  orderStatus: OrderStatus;
  isActive: boolean;
  userId: number;
  autoSellFund: boolean;
  scheduledExecutionTime: string;
  // Genuinely null when there's no schedule - scheduledExecutionTime above is "-" in that case (a
  // display placeholder, and a truthy JS string), so logic that needs to tell "no schedule" apart from
  // "has one" (e.g. the Edit form) must use this field, not that one.
  scheduledExecutionTimeRaw: string | null;
  volumeCalculationType: VolumeCalculationType | null;
  volumeCalculationValue: number | null;
}

export interface IManualTablesSearchParam {
  sahraUserId?: number;
  instrumentId?: string;
  portfolioGroupId?: number;
}

export interface IManualSahraOrderTableSearchParam extends ISearchParameters {
  orderId?: number;
  shekarOrderId?: number;
  stepOrderId?: number;
  manualStepOrderId?: number;
  manualOrderId?: number;
}

export interface IManualSahraOrderTableReport extends ITableReport {
  buyInsName: string;
  sellInsName: string;
  insName: string;
  type: string;
  orderPrice: number;
  orderPriceStr: string;
  price: string;
  creationTime: string;
  updateTime: string;
  exeTime: number;
  exeStr: string;
  fill: number;
  total: number;
  pending: number;
  isActive: boolean;
  errorMsg: string;
}

export interface IManualSubOrdersTableReport extends ITableReport {
  sahraUserId: number;
  sahraName: string;
  operator: string;
  orderSideStr: string;
  namad: string;
  totalSize: number;
  filledVolume: number;
  orderSide: StepOrderSide;
  instrument: string;
  isActive: true;
}

export interface IManualTradeTableReport extends ITableReport {
  sahraName: string;
  operator: string;
  namad: string;
  orderSideStr: string;
  price: number;
  orderSide: StepOrderSide;
  volume: number;
  sahraUserId: number;
  instrumentType: StepInstrumentType;
  instrument: string;
  baseInstrument: string;
  optionInstrument: string;
  activeStep: number;
  scheduledExecutionTime: string;
}

export interface IManualActionsTableSearchParam extends ISearchParameters {
  manualOrderId: number | null;
  manualStepOrderId: number | null;
}
export interface IManualActionsTableReport extends ITableReport {}

export enum ManualScheduleMode {
  Immediate = 0,
  Scheduled = 1,
}

export interface IEditManualOrderFormValues {
  manualStepOrderId: number;
  fromPrice: number;
  toPrice: number;
  stepCount: number;
  totalVolume: number | null;
  manualStepOrderIds: number[] | null;
  applyToAll: boolean | null;
  autoSellFund: boolean | null;
  scheduleMode: ManualScheduleMode | null;
  scheduledExecutionTime: string | null;
  volumeCalculationType: VolumeCalculationType | null;
  volumeCalculationValue: number | null;
}

// Read-only: asks the server to re-run a volume-calculation method (e.g. "2% of portfolio") against
// the order's live account data, so the edit form can show the resulting share count before submit.
export interface IPreviewEditVolumeRequest {
  manualStepOrderId: number;
  volumeCalculationType: VolumeCalculationType;
  volumeCalculationValue: number;
  fromPrice: number;
  toPrice: number;
}

export interface IPreviewEditVolumeResult {
  success: boolean;
  // For TargetPortfolioPercent, volume is the additional amount from the current holding.
  volume: number | null;
  totalVolume: number | null;
  remainedVolume: number | null;
  msg: string | null;
}

export interface IFailedManualStepOrderEditItem {
  manualStepOrderId: number;
  name: string | null;
  initialVolume: number;
  doneVolume: number;
  requestedVolume: number;
  reason: string;
}

export interface IEditManualStepOrderResult {
  data: boolean;
  msg: string;
  failedOrders: IFailedManualStepOrderEditItem[];
}

export interface IManualOrderPreviewRow {
  userId: number;
  userName: string;
  orderSide: StepOrderSide;
  instrumentId: string;
  instrumentName: string;
  instrumentType: StepInstrumentType;
  firstStepPrice: number;
  lastStepPrice: number;
  stepCount: number;
  orderStartegyStr?: string;
  volumeCalcMethod: string;
  originalVolumeInput: string;
  totalVolume: number;
  engagedCapitalAmount: number;
  autoSellFund: boolean;
  scheduledExecutionTime?: string | null;
  optionOrderType?: StepOptionOrderType | null;
  previewStatus?: "Offline" | "Timeout" | "BrokenOrder" | "IncompleteNav" | "NoConnectedUser" | "Deduplicated" | null;
  previewMessage?: string | null;
}

export interface IManualOrderPreviewOrderDto {
  userIds: number[];
  orderSide: StepOrderSide;
  optionOrderType: StepOptionOrderType | null;
  optionOrderTypeEnum: unknown;
  instrumentType: StepInstrumentType;
  instrumentId: string;
  fromPrice: number;
  toPrice: number;
  stepCount: number;
  volume: number;
  volumeCalculationType: number;
  volumeCalculationTypeEnum: VolumeCalculationType;
  volumePerUser: Record<string, number>;
  autoSellFund?: boolean;
  scheduledExecutionTime?: string | null;
  // The method/value as the user actually entered them (e.g. PortfolioPercent + 1) - distinct from
  // volumeCalculationType/volume above, which this same object also carries but already resolved to
  // FixedAmount + the computed count for Submit. Echoed straight back from Preview's response so
  // Submit (and AddStepOrder, which persists these two on the order) still sees the real input.
  originalVolumeCalculationType?: number | null;
  originalVolumeAmount?: number | null;
}

export interface IManualOrderPreview {
  rows: IManualOrderPreviewRow[];
  orderDto: IManualOrderPreviewOrderDto;
  timedOutUserIds?: number[];
  brokenOrderUserIds?: number[];
  incompleteNavUserIds?: number[];
  previewWarnings?: string[];
  volumeSummary?: IOrderVolumeSummaryItem[];
}
